// sim/regionData.ts — the WORLD MAP: the seven regions, their adjacency, unlock
// thresholds, and per-region spawn tables (resources / enemies / gene drops).
// Pure data, no logic (the RegionManager in regions.ts reads it). Indexed
// positionally by the REGION constant — that order is a hard contract.
// Grounded in docs/LORE.md §"Regions — the expanding world".

import { GENE_TYPE_COUNT } from '../types.ts';
import type { Region } from './regions.ts';
import { REGION, REGION_COUNT, RegionManager } from './regions.ts';

/** A resource a region yields: a named food source with a draw weight + amount. */
export interface ResourceEntry {
  readonly name: string;
  readonly weight: number;
  readonly foodAmount: number;
}

/** An enemy a region spawns: a named threat with a difficulty + draw weight. */
export interface EnemyEntry {
  readonly name: string;
  readonly threat: number;
  readonly weight: number;
}

/** Everything a region IS: identity, gating, adjacency, and its spawn tables. */
export interface RegionDef {
  readonly region: Region;
  readonly name: string;
  readonly glyph: string;
  readonly description: string;
  /** Colony strength required before this region can be unlocked. */
  readonly unlockStrength: number;
  /** Logically-adjacent regions (undirected; expansion flows along these). */
  readonly neighbors: readonly Region[];
  readonly resources: readonly ResourceEntry[];
  readonly enemies: readonly EnemyEntry[];
  /** Draw weights over GENE_TYPE (Wing/Mandible/VenomGland/Carapace/Antenna/Limb). */
  readonly geneWeights: readonly number[];
  /** Added to rarity rolls here — deeper, harder regions drop better genes. */
  readonly rarityBias: number;
}

// Columns for geneWeights:  Wing  Mandible  VenomGland  Carapace  Antenna  Limb
export const REGION_DEFS: readonly RegionDef[] = [
  {
    region: REGION.Backyard,
    name: 'Backyard',
    glyph: 'B',
    description: 'Trampled grass and patio cracks — the brood\'s first, safe ground.',
    unlockStrength: 0, // the starter zone is always open
    neighbors: [REGION.Garden, REGION.CompostHeap],
    resources: [
      { name: 'Crumbs', weight: 8, foodAmount: 10 },
      { name: 'Seeds', weight: 6, foodAmount: 12 },
      { name: 'Dew', weight: 4, foodAmount: 6 },
    ],
    enemies: [
      { name: 'Rival ant', threat: 2, weight: 8 },
      { name: 'Ground beetle', threat: 3, weight: 5 },
      { name: 'Earwig', threat: 2, weight: 4 },
    ],
    geneWeights: [2, 3, 2, 3, 8, 12],
    rarityBias: 0,
  },
  {
    region: REGION.Garden,
    name: 'Garden',
    glyph: 'G',
    description: 'Flowerbeds heavy with nectar and aphids — rich, but patrolled by wasps.',
    unlockStrength: 12,
    neighbors: [REGION.Backyard, REGION.Pond, REGION.Shed],
    resources: [
      { name: 'Nectar', weight: 8, foodAmount: 16 },
      { name: 'Aphid herd', weight: 7, foodAmount: 14 },
      { name: 'Petals', weight: 5, foodAmount: 8 },
    ],
    enemies: [
      { name: 'Wasp', threat: 5, weight: 6 },
      { name: 'Garden spider', threat: 6, weight: 5 },
      { name: 'Caterpillar', threat: 1, weight: 4 },
      { name: 'Beetle', threat: 3, weight: 5 },
      { name: 'Dragonfly', threat: 6, weight: 3 },
    ],
    geneWeights: [10, 3, 8, 2, 6, 3],
    rarityBias: 1,
  },
  {
    region: REGION.CompostHeap,
    name: 'Compost Heap',
    glyph: 'C',
    description: 'A warm, rotting mound — endless food, crawling with hungry decomposers.',
    unlockStrength: 18,
    neighbors: [REGION.Backyard, REGION.Shed],
    resources: [
      { name: 'Rot', weight: 9, foodAmount: 18 },
      { name: 'Fungus', weight: 6, foodAmount: 12 },
      { name: 'Peelings', weight: 5, foodAmount: 10 },
    ],
    enemies: [
      { name: 'Maggot swarm', threat: 3, weight: 7 },
      { name: 'Centipede', threat: 6, weight: 5 },
      { name: 'Rove beetle', threat: 4, weight: 4 },
      { name: 'Beetle', threat: 3, weight: 6 },
    ],
    geneWeights: [2, 12, 8, 4, 3, 5],
    rarityBias: 1,
  },
  {
    region: REGION.Shed,
    name: 'Shed',
    glyph: 'S',
    description: 'Dry timber and spilled grain in the dark — armoured pests hold the corners.',
    unlockStrength: 40,
    neighbors: [REGION.Garden, REGION.CompostHeap, REGION.HouseInterior],
    resources: [
      { name: 'Grain', weight: 8, foodAmount: 20 },
      { name: 'Oil drops', weight: 4, foodAmount: 8 },
      { name: 'Sawdust', weight: 5, foodAmount: 6 },
    ],
    enemies: [
      { name: 'House mouse', threat: 7, weight: 5 },
      { name: 'Cellar spider', threat: 5, weight: 6 },
      { name: 'Woodlouse', threat: 1, weight: 4 },
    ],
    geneWeights: [3, 10, 3, 12, 4, 5],
    rarityBias: 2,
  },
  {
    region: REGION.Pond,
    name: 'Pond',
    glyph: 'P',
    description: 'A still green edge — larvae-rich shallows under the wings of dragonflies.',
    unlockStrength: 28,
    neighbors: [REGION.Garden],
    resources: [
      { name: 'Algae', weight: 7, foodAmount: 14 },
      { name: 'Larvae', weight: 8, foodAmount: 16 },
      { name: 'Reeds', weight: 4, foodAmount: 9 },
    ],
    enemies: [
      { name: 'Frog', threat: 8, weight: 5 },
      { name: 'Water beetle', threat: 5, weight: 6 },
      { name: 'Dragonfly nymph', threat: 6, weight: 4 },
      { name: 'Dragonfly', threat: 7, weight: 5 },
      { name: 'Beetle', threat: 3, weight: 4 },
    ],
    geneWeights: [12, 3, 9, 4, 5, 3],
    rarityBias: 2,
  },
  {
    region: REGION.HouseInterior,
    name: 'House interior',
    glyph: 'H',
    description: 'Warmth, pantries, and sugar — the richest larder, stalked by house spiders.',
    unlockStrength: 60,
    neighbors: [REGION.Shed, REGION.RoofGutters],
    resources: [
      { name: 'Sugar', weight: 9, foodAmount: 22 },
      { name: 'Pantry crumbs', weight: 7, foodAmount: 18 },
      { name: 'Grease', weight: 4, foodAmount: 10 },
    ],
    enemies: [
      { name: 'House spider', threat: 7, weight: 6 },
      { name: 'Silverfish', threat: 3, weight: 7 },
      { name: 'Cockroach', threat: 4, weight: 5 },
    ],
    geneWeights: [3, 9, 5, 9, 7, 4],
    rarityBias: 3,
  },
  {
    region: REGION.RoofGutters,
    name: 'Roof / gutters',
    glyph: 'R',
    description: 'The wind-scoured high frontier — pollen and trapped prey, hunted by birds.',
    unlockStrength: 85,
    neighbors: [REGION.HouseInterior],
    resources: [
      { name: 'Pollen', weight: 6, foodAmount: 12 },
      { name: 'Trapped insects', weight: 7, foodAmount: 15 },
      { name: 'Leaf litter', weight: 5, foodAmount: 8 },
    ],
    enemies: [
      { name: 'Sparrow', threat: 9, weight: 5 },
      { name: 'Hornet', threat: 8, weight: 6 },
      { name: 'Crane fly', threat: 2, weight: 4 },
    ],
    geneWeights: [14, 4, 6, 3, 6, 3],
    rarityBias: 4,
  },
];

// --- Length / contract assertions (evaluated at import — catch mis-edits). ---
const _assertRegionData = (() => {
  if (REGION_DEFS.length !== REGION_COUNT) {
    throw new Error(`REGION_DEFS length ${REGION_DEFS.length} !== REGION_COUNT ${REGION_COUNT}`);
  }
  for (let r = 0; r < REGION_DEFS.length; r++) {
    const def = REGION_DEFS[r] as RegionDef;
    if (def.region !== r) throw new Error(`REGION_DEFS[${r}].region is ${def.region}, must equal index`);
    if (def.geneWeights.length !== GENE_TYPE_COUNT) {
      throw new Error(`REGION_DEFS[${r}].geneWeights length ${def.geneWeights.length} !== ${GENE_TYPE_COUNT}`);
    }
    // Adjacency must be symmetric: every neighbour lists this region back.
    for (const nb of def.neighbors) {
      const back = (REGION_DEFS[nb] as RegionDef).neighbors;
      if (!back.includes(def.region)) {
        throw new Error(`adjacency not symmetric: ${r} -> ${nb} but not back`);
      }
    }
  }
})();

/** Build a RegionManager wired to the canonical region map. */
export function newRegionManager(): RegionManager {
  return new RegionManager(REGION_DEFS);
}
